import { ProfileDetail } from "@garther/interfaces";
import { UpdateUserProfile } from "@garther/interfaces";
import { create } from "zustand";

import { setUserId, setUserProperties, trackEngagement, trackLogin } from "@/libs/analytics";
import { profileApiClient } from "@/libs/api/profile-client";
import { FirebaseUser, firebaseService } from "@/libs/firebase";

interface AuthUser {
  fbUser: FirebaseUser | null;
  profile: ProfileDetail | null;
}

interface AuthState {
  curUser: AuthUser;
  loggedIn: boolean;
  loading: boolean;
  // Actions
  signIn: (redirectUri?: string) => Promise<void>;
  setUser: (user: Partial<AuthUser>) => void;
  setLoggedIn: (loggedIn: boolean) => void;
  setLoading: (loading: boolean) => void;
  setProfile: (profile: ProfileDetail | null) => void;
  loadProfile: () => Promise<void>;
  updateProfile: (profileData: UpdateUserProfile) => Promise<void>;
  reset: () => void;
}

const dummyUser: AuthUser = {
  fbUser: null,
  profile: null
};

export const useAuthStore = create<AuthState>((set, get) => ({
  curUser: dummyUser,
  loggedIn: false,
  loading: true,

  signIn: async (redirectUri?: string) => {
    trackEngagement("sign_in_click", "authentication");
    firebaseService.signIn(redirectUri);
  },

  setUser: (user: Partial<ProfileDetail>) => {
    set((state) => ({
      curUser: { ...state.curUser, ...user }
    }));
  },

  setLoggedIn: (loggedIn: boolean) => {
    set({ loggedIn });
  },

  setLoading: (loading: boolean) => {
    set({ loading });
  },

  setProfile: (profile: ProfileDetail | null) => {
    set((state) => ({
      curUser: { ...state.curUser, profile }
    }));
  },

  loadProfile: async () => {
    try {
      const profile = await profileApiClient.getMyProfile();
      set((state) => ({
        curUser: { ...state.curUser, profile }
      }));
      console.log("[DEBUG] Profile loaded:", profile);
    } catch (error) {
      console.error("Failed to load profile:", error);
      set((state) => ({
        curUser: { ...state.curUser, profile: null }
      }));
    }
  },

  updateProfile: async (profileData: UpdateUserProfile) => {
    try {
      const currentProfile = get().curUser.profile;
      if (!currentProfile) {
        throw new Error("No profile to update");
      }

      const updatedProfile = await profileApiClient.updateMyProfile(profileData);
      set((state) => ({
        curUser: { ...state.curUser, profile: updatedProfile }
      }));
    } catch (error) {
      console.error("Failed to update profile:", error);
      throw error;
    }
  },

  reset: () => {
    set({
      curUser: dummyUser,
      loggedIn: false,
      loading: true
    });
  }
}));

// Initialize auth listeners
export const initializeAuthListeners = async () => {
  // Firebase handler
  const fbHandler = (u: FirebaseUser | null) => {
    console.log("[DEBUG] Firebase user authenticated - handler:", u);
    if (!!u) {
      useAuthStore.getState().setUser({ fbUser: u });
      useAuthStore.getState().setLoggedIn(true);

      // Track login and set user properties
      trackLogin("firebase");
      setUserId(u.uid);
      setUserProperties({
        user_id: u.uid,
        user_type: "firebase",
        email: u.email || undefined
      });
    } else {
      useAuthStore.getState().setUser({ fbUser: null });
      useAuthStore.getState().setLoggedIn(false);
    }
    useAuthStore.getState().setLoading(false);
  };

  // Subscribe to auth changes
  await firebaseService.onAuthStateChanged(fbHandler);
};
